import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import { Box, Button, Divider, List, Paper, Skeleton, Typography } from '@mui/material';
import { useI18N } from '../i18n';
import { useIsMobile } from '../hooks';
import {
	getGroups,
	createGroup,
	deleteGroup,
	useGroups,
	Group,
	updateGroup,
} from '../redux/slices/groups';
import { useDispatch } from 'react-redux';
import { DeleteOutline, Edit } from '@mui/icons-material';
import { useRedux } from '../redux';
import { useDialog } from '../redux/slices/dialog';

const GroupsView = () => {
	const { dialog, snackbar } = useRedux();
	const { action: dialogAction, data: dialogData } = useDialog();
	const i18n = useI18N();
	const isMobile = useIsMobile();
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const { groups, loading: loadingGroups } = useGroups();

	useEffect(() => {
		dispatch(getGroups());
	}, [dispatch]);

	useEffect(() => {
		if (dialogAction === 'create-group' && dialogData.name && dialogData.emailNotification) {
			const groupData: Group = {
				name: dialogData.name as string,
				emailNotification: dialogData.emailNotification as string,
				phoneNumbers: dialogData.phoneNumbers as string[],
				isFranchise: (dialogData.isFranchise as boolean) || false,
			};
			dispatch(createGroup(groupData));
			dialog.update({ data: {} });
		}
	}, [dialogAction, dialogData]);

	useEffect(() => {
		if (
			dialogAction === 'update-group' &&
			dialogData.id &&
			dialogData.name &&
			dialogData.phoneNumbers &&
			dialogData.emailNotification
		) {
			const originalGroup = groups.find((g) => g.id === dialogData.id);
			if (
				originalGroup &&
				(originalGroup.name !== dialogData.name ||
					originalGroup.emailNotification !== dialogData.emailNotification ||
					originalGroup.isFranchise !== dialogData.isFranchise ||
					originalGroup.phoneNumbers !== dialogData.phoneNumbers)
			) {
				const updatedGroup: Group = {
					id: dialogData.id as string,
					name: dialogData.name as string,
					emailNotification: dialogData.emailNotification as string,
					phoneNumbers: dialogData.phoneNumbers as string[],
					isFranchise: (dialogData.isFranchise as boolean) || false,
				};

				dispatch(updateGroup(updatedGroup));
			}
		}
	}, [dialogAction, dialogData]);

	return (
		<Paper sx={{ p: 2 }}>
			<Box>
				<Button
					variant="contained"
					startIcon={<AddCircleIcon />}
					onClick={() => {
						dialog.update({ data: {} });
						dialog.show({ action: 'create-group' });
					}}
				>
					{i18n('dialogs.create-group.title')}
				</Button>
			</Box>
			<Box>
				{groups.length === 0 && <Typography sx={{ mt: 4 }}>{i18n('groups.list.empty')}</Typography>}
				{loadingGroups ? (
					<List>
						{new Array(5).fill(null).map((ticket, i) => (
							<Box key={i}>
								<Divider />

								<Typography fontWeight="bold" noWrap>
									<Skeleton sx={{ p: 4 }} variant="text" width={isMobile ? 150 : 300} />
								</Typography>
							</Box>
						))}
					</List>
				) : (
					<List sx={{ mt: 2, width: '50%' }}>
						{groups &&
							groups.map((group) => (
								<Box
									key={group.id}
									sx={{
										display: 'flex',
										justifyContent: 'space-between',
										alignItems: 'center',
										p: 4,
										width: '100%',
										borderRadius: 2,
										backgroundColor: 'rgba(0, 0, 0, 0.05)',
										marginBottom: 2,
									}}
								>
									<Box sx={{ display: 'flex', flexDirection: 'column' }}>
										<Typography fontWeight="bold">{group.name}</Typography>
										<Typography>{group.emailNotification}</Typography>
										{group.phoneNumbers &&
											group.phoneNumbers.map((phone) => (
												<Typography key={phone} variant="caption">
													{phone}
												</Typography>
											))}
									</Box>
									<Box sx={{ display: 'flex', gap: 1 }}>
										<Edit
											color="primary"
											sx={{ cursor: 'pointer' }}
											onClick={() => {
												dialog.update({
													data: {
														id: group.id,
														name: group.name,
														emailNotification: group.emailNotification,
														phoneNumbers: group.phoneNumbers,
														isFranchise: group.isFranchise,
													},
												});
												dialog.show({ action: 'update-group' });
											}}
										/>
										<DeleteOutline
											color="error"
											sx={{ cursor: 'pointer' }}
											onClick={() => {
												dispatch(deleteGroup(group.id!));
											}}
										/>
									</Box>
								</Box>
							))}
					</List>
				)}
			</Box>
		</Paper>
	);
};

export default GroupsView;
